import React, { useState } from "react";
import { MdEdit, MdDelete, MdRestaurant } from "react-icons/md";
import Card from "components/card";
import { Recipe } from "../../types/recipe";
import { getMealTypeLabel, getMealTypeColor } from "../../utils/recipeUtils";
import { EMPTY_VALUE } from "../../utils/formatters";
import EmptyState from "../common/EmptyState";
import SearchForm from "../common/SearchForm";
import LoadingSpinner from "../common/LoadingSpinner";
import { formatNutrientAmount, NutrientKey } from "./nutrientMeta";

interface RecipesTableProps {
  recipes: Recipe[];
  loading: boolean;
  onEdit: (recipe: Recipe) => void;
  onDelete: (recipe: Recipe) => void;
  onView: (recipe: Recipe) => void;
  onSearch: (searchTerm: string) => void;
  onClearSearch: () => void;
  searchTerm: string;
  isSearchMode: boolean;
}

const RecipesTable: React.FC<RecipesTableProps> = ({
  recipes,
  loading,
  onEdit,
  onDelete,
  onView,
  onSearch,
  onClearSearch,
  searchTerm,
  isSearchMode,
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || "");

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(localSearchTerm.trim());
  };

  const handleClearSearch = () => {
    setLocalSearchTerm("");
    onClearSearch();
  };

  const formatNutrition = (key: NutrientKey, value?: number) => {
    if (value === null || value === undefined) return EMPTY_VALUE;
    return `${formatNutrientAmount(key, value)}g`;
  };

  const formatCalories = (value?: number) => {
    if (value === null || value === undefined || value === 0)
      return EMPTY_VALUE;
    return `${formatNutrientAmount("calories", value)} kcal`;
  };

  const truncateText = (text: string, maxLength: number = 80) => {
    if (!text) return EMPTY_VALUE;
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  return (
    <Card extra="w-full h-full p-3 md:p-6 sm:overflow-x-auto">
      <div className="relative flex flex-col items-start justify-between space-y-4 pb-4 sm:flex-row sm:items-center sm:space-y-0">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Recipe Library
        </div>

        {/* Search Bar */}
        <div className="w-full sm:w-auto">
          <SearchForm
            value={localSearchTerm}
            onChange={setLocalSearchTerm}
            onSubmit={handleSearchSubmit}
            onClear={handleClearSearch}
            placeholder="Search recipes..."
            hasSearched={isSearchMode}
            loading={loading}
          />
          {isSearchMode && (
            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              Showing search results for "{searchTerm}"
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner bare size="lg" />
        </div>
      )}

      {!loading && recipes.length === 0 && (
        <EmptyState
          icon={MdRestaurant}
          title={isSearchMode ? "No recipes found" : "No recipes yet"}
          description={
            isSearchMode
              ? "Try adjusting your search criteria or create a new recipe."
              : "Get started by creating your first recipe."
          }
        />
      )}

      {!loading && recipes.length > 0 && (
        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-navy-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Recipe
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Nutrition
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-navy-900">
              {recipes.map((recipe) => (
                <tr
                  key={recipe.id}
                  onClick={() => onView(recipe)}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-800"
                >
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center">
                      <div className="h-20 w-32 flex-shrink-0">
                        {recipe.recipe_image?.full_path ? (
                          <img
                            src={recipe.recipe_image.full_path}
                            alt={recipe.name}
                            className="h-20 w-32 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="flex h-20 w-32 items-center justify-center rounded-lg bg-biafit-primary">
                            <MdRestaurant className="h-10 w-10 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {recipe.name}
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {formatCalories(recipe.calories)}
                          </div>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getMealTypeColor()}`}
                          >
                            {getMealTypeLabel(recipe.meal_type)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-white">
                      <div className="flex space-x-4">
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900 dark:text-green-200">
                          P: {formatNutrition("protein", recipe.protein)}
                        </span>
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          C: {formatNutrition("carbs", recipe.carbs)}
                        </span>
                        <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                          F: {formatNutrition("fat", recipe.fat)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="max-w-xs text-sm text-gray-900 dark:text-white">
                      {truncateText(recipe.description || "")}
                    </div>
                  </td>
                  <td
                    className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => onEdit(recipe)}
                        className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                        title="Edit Recipe"
                      >
                        <MdEdit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => onDelete(recipe)}
                        className="text-gray-600 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                        title="Delete Recipe"
                      >
                        <MdDelete className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && recipes.length > 0 && (
        <div className="mt-6 space-y-4 md:hidden">
          {recipes.map((recipe) => (
            <div
              key={recipe.id}
              className="cursor-pointer rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-navy-900 md:p-4"
              onClick={() => onView(recipe)}
            >
              <div className="mb-3 flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="h-16 w-28 flex-shrink-0">
                    {recipe.recipe_image?.full_path ? (
                      <img
                        src={recipe.recipe_image.full_path}
                        alt={recipe.name}
                        className="h-16 w-28 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="flex h-16 w-28 items-center justify-center rounded-lg bg-biafit-primary">
                        <MdRestaurant className="h-8 w-8 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="mb-1 text-sm font-medium leading-4 text-navy-700 dark:text-white">
                      {recipe.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatCalories(recipe.calories)}
                      </p>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getMealTypeColor()}`}
                      >
                        {getMealTypeLabel(recipe.meal_type)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(recipe);
                    }}
                    className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    title="Edit Recipe"
                  >
                    <MdEdit className="h-5 w-5" />
                  </button>
                  {/* <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(recipe)
                    }}
                    className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    title="Delete Recipe"
                  >
                    <MdDelete className="h-5 w-5" />
                  </button> */}
                </div>
              </div>

              <div className="mb-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900 dark:text-green-200">
                  Protein: {formatNutrition("protein", recipe.protein)}
                </span>
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  Carbs: {formatNutrition("carbs", recipe.carbs)}
                </span>
                <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                  Fat: {formatNutrition("fat", recipe.fat)}
                </span>
              </div>

              {recipe.description && (
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {truncateText(recipe.description, 100)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default RecipesTable;
